import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  QrCode, 
  Camera, 
  ArrowRight, 
  Search, 
  Maximize, 
  AlertCircle,
  HelpCircle,
  X
} from 'lucide-react';
import { Card, Button, Input, Badge } from '../../components/ui/index.jsx';
import { Html5Qrcode } from 'html5-qrcode';

const Scanner = () => {
    const navigate = useNavigate();
    const [manualCode, setManualCode] = useState('');
    const [error, setError] = useState(null);
    const scannerRef = useRef(null);
    const [isScanning, setIsScanning] = useState(false);

    useEffect(() => {
        const scannerId = "reader";
        const html5QrCode = new Html5Qrcode(scannerId);
        scannerRef.current = html5QrCode;

        const startScanner = async () => {
            try {
                setIsScanning(true);
                const config = { 
                    fps: 10, 
                    qrbox: { width: 250, height: 250 },
                    aspectRatio: 1.0
                };

                await html5QrCode.start(
                    { facingMode: "environment" },
                    config,
                    (decodedText) => {
                        // Success callback
                        html5QrCode.stop().then(() => {
                            navigate(`/dashboard/quick-payment/${decodedText}`);
                        }).catch(err => {
                            console.error("Failed to stop scanner", err);
                            navigate(`/dashboard/quick-payment/${decodedText}`);
                        });
                    },
                    (errorMessage) => {
                        // Error callback (usually just 'no QR code found' noise)
                    }
                );
            } catch (err) {
                console.error("Unable to start scanning", err);
                setError("تعذر الوصول إلى الكاميرا. يرجى التأكد من منح الأذونات اللازمة.");
                setIsScanning(false);
            }
        };

        startScanner();

        return () => {
            if (scannerRef.current && scannerRef.current.isScanning) {
                scannerRef.current.stop().catch(err => console.error(err));
            }
        };
    }, [navigate]);

    const handleManualSubmit = (e) => {
        e.preventDefault();
        if (manualCode.trim()) {
            navigate(`/dashboard/quick-payment/${manualCode}`);
        }
    };

    return (
        <div className="max-w-2xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-700">
            {/* Header */}
            <div className="flex items-center justify-between px-2">
                <div>
                    <h2 className="text-2xl text-slate-800 font-normal">ماسح الـ QR</h2>
                    <p className="text-slate-400 text-[11px] mt-1 font-normal">قم بتوجيه الكاميرا نحو رمز الطالب للمعالجة الفورية</p>
                </div>
                <Badge variant={error ? "danger" : "primary"} className="px-3 py-1 font-normal">
                    {error ? "خطأ في الكاميرا" : "وضع المسح النشط"}
                </Badge>
            </div>

            <div className="grid grid-cols-1 gap-6">
                {/* Scanner Viewport */}
                <Card className="relative aspect-square md:aspect-video overflow-hidden bg-slate-950 border-none shadow-2xl flex items-center justify-center group">
                    {/* The div where html5-qrcode will render */}
                    <div id="reader" className="w-full h-full [&>video]:object-cover"></div>

                    {error && (
                        <div className="absolute inset-0 z-10 bg-slate-900/90 flex flex-col items-center justify-center p-8 text-center">
                            <AlertCircle className="w-12 h-12 text-red-500 mb-4" />
                            <p className="text-white text-sm font-normal mb-6">{error}</p>
                            <Button variant="outline" onClick={() => window.location.reload()} className="border-white/20 text-white hover:bg-white/10">
                                إعادة المحاولة
                            </Button>
                        </div>
                    )}

                    {/* Scanner Overlay UI (Custom) */}
                    {!error && (
                        <div className="absolute inset-0 pointer-events-none flex flex-col items-center justify-center z-10">
                            {/* Target Frame */}
                            <div className="w-64 h-64 border-2 border-white/20 rounded-3xl relative">
                                {/* Corners */}
                                <div className="absolute -top-0.5 -left-0.5 w-8 h-8 border-t-4 border-l-4 border-primary rounded-tl-xl" />
                                <div className="absolute -top-0.5 -right-0.5 w-8 h-8 border-t-4 border-r-4 border-primary rounded-tr-xl" />
                                <div className="absolute -bottom-0.5 -left-0.5 w-8 h-8 border-b-4 border-l-4 border-primary rounded-bl-xl" />
                                <div className="absolute -bottom-0.5 -right-0.5 w-8 h-8 border-b-4 border-r-4 border-primary rounded-br-xl" />
                                
                                {/* Scanning Animation Line */}
                                <div className="absolute top-0 left-0 w-full h-0.5 bg-primary/40 shadow-[0_0_15px_rgba(238,165,8,0.6)] animate-scan-line" />
                            </div>

                            <div className="mt-8 bg-black/40 backdrop-blur-md px-6 py-2 rounded-full border border-white/10">
                                <p className="text-[10px] text-white uppercase tracking-widest flex items-center gap-2">
                                    <span className={`w-1.5 h-1.5 rounded-full bg-primary ${isScanning ? 'animate-pulse' : ''}`} />
                                    جاري البحث عن رمز...
                                </p>
                            </div>
                        </div>
                    )}
                </Card>

                {/* Manual Entry Part */}
                <Card className="p-6 bg-white border-slate-100 relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-slate-100" />
                    <div className="flex items-center justify-between mb-6">
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-slate-50 rounded-xl flex items-center justify-center text-slate-400">
                                <Search size={20} />
                            </div>
                            <div>
                                <h3 className="text-sm text-slate-800 font-normal">إدخال يدوي</h3>
                                <p className="text-[10px] text-slate-400 font-normal">في حال تعذر المسح، أدخل رمز الطالب هنا</p>
                            </div>
                        </div>
                    </div>

                    <form onSubmit={handleManualSubmit} className="flex gap-3">
                        <div className="flex-1">
                            <Input 
                                placeholder="أدخل رقم الـ QR أو رقم التلميذ..." 
                                value={manualCode}
                                onChange={(e) => setManualCode(e.target.value)}
                                className="h-12 bg-slate-50/50 border-slate-200"
                            />
                        </div>
                        <Button 
                            type="submit" 
                            variant="primary" 
                            className="px-6 h-12 shadow-lg shadow-primary/20"
                            disabled={!manualCode.trim()}
                        >
                            دخول
                        </Button>
                    </form>
                </Card>

                {/* Instructions */}
                <div className="text-center space-y-2 py-4">
                    <p className="text-[11px] text-slate-400 font-normal">نصيحة: تأكد من وجود إضاءة كافية ووضوح رمز الـ QR</p>
                    <button 
                        onClick={() => navigate('/dashboard')}
                        className="text-xs text-slate-400 hover:text-primary transition-colors font-normal"
                    >
                        إلغاء والعودة للوحة التحكم
                    </button>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{ __html: `
                @keyframes scan-line {
                    0% { top: 0%; opacity: 0; }
                    10% { opacity: 1; }
                    90% { opacity: 1; }
                    100% { top: 100%; opacity: 0; }
                }
                .animate-scan-line {
                    animation: scan-line 3s infinite linear;
                }
                /* Hide html5-qrcode technical UI */
                #reader__scan_region {
                    background: transparent !important;
                }
                #reader__dashboard {
                    display: none !important;
                }
                #reader video {
                    width: 100% !important;
                    height: 100% !important;
                    object-fit: cover !important;
                }
            `}} />
        </div>
    );
};

export default Scanner;
